import { motion, AnimatePresence } from 'motion/react';
import { MessageSquare, Phone, Globe, Shield, Zap, ArrowRight, QrCode, BookOpen } from 'lucide-react';
import AuraAgent from './AuraAgent';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DocsAndTutorials from './DocsAndTutorials';

export default function Support() {
  const [showChat, setShowChat] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white selection:bg-yellow-500/30 overflow-x-hidden">
      {/* Background Decor */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-yellow-500/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-white/5 blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-lg mx-auto px-6 pt-12 pb-24">
        {showDocs ? (
          <DocsAndTutorials onBack={() => setShowDocs(false)} />
        ) : (
          <>
            {/* Header */}
            <div className="flex flex-col items-center text-center mb-12">
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="mb-6 h-16 w-16 rounded-2xl bg-gradient-to-br from-white to-white/5 p-[1px]"
              >
                <div className="flex h-full w-full items-center justify-center rounded-2xl bg-black">
                  <Zap className="text-white fill-white" size={32} />
                </div>
              </motion.div>
              <motion.h1 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="text-4xl font-black tracking-tighter uppercase mb-2"
              >
                Soporte <span className="text-white/40">Aura</span>
              </motion.h1>
              <motion.p 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-white/40 text-sm font-medium tracking-wide"
              >
                Estamos aquí para que tu negocio nunca se detenga.
              </motion.p>
            </div>

            {/* Action Cards */}
            <div className="space-y-4 mb-12">
              <motion.button
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                onClick={() => setShowChat(true)}
                className="w-full group relative overflow-hidden rounded-3xl bg-white p-8 text-black transition-all hover:scale-[1.02] active:scale-95"
              >
                <div className="relative z-10 flex items-center justify-between">
                  <div className="text-left">
                    <span className="text-[10px] font-black uppercase tracking-widest text-black/40 mb-1 block">Solución Instantánea</span>
                    <h3 className="text-2xl font-black tracking-tight">Chat con Aura AI</h3>
                  </div>
                  <MessageSquare size={32} className="opacity-20 group-hover:opacity-100 transition-opacity" />
                </div>
              </motion.button>

              <motion.a
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                href="https://wa.me/34648512127"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-between rounded-3xl bg-white/5 border border-white/10 p-6 transition-all hover:bg-white/10 active:scale-95"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-green-500/10 flex items-center justify-center">
                    <Phone className="text-green-500" size={20} />
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-sm">WhatsApp Soporte</h4>
                    <p className="text-[10px] text-white/40 uppercase tracking-widest font-black">Atención Directa</p>
                  </div>
                </div>
                <ArrowRight size={16} className="text-white/20" />
              </motion.a>

              <motion.button
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                onClick={() => navigate('/')}
                className="w-full flex items-center justify-between rounded-3xl bg-white/5 border border-white/10 p-6 transition-all hover:bg-white/10 active:scale-95"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                    <Globe className="text-blue-500" size={20} />
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-sm">Inicio</h4>
                    <p className="text-[10px] text-white/40 uppercase tracking-widest font-black">Portal Aura Business</p>
                  </div>
                </div>
                <ArrowRight size={16} className="text-white/20" />
              </motion.button>

              <motion.button
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                onClick={() => setShowDocs(true)}
                className="w-full flex items-center justify-between rounded-3xl bg-white/5 border border-white/10 p-6 transition-all hover:bg-white/10 active:scale-95"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-yellow-500/10 flex items-center justify-center">
                    <BookOpen className="text-yellow-500" size={20} />
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-sm">Documentación</h4>
                    <p className="text-[10px] text-white/40 uppercase tracking-widest font-black">Tutoriales y Guías</p>
                  </div>
                </div>
                <ArrowRight size={16} className="text-white/20" />
              </motion.button>
            </div>
          </>
        )}

        {/* Quick Links / FAQ Mini */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="grid grid-cols-2 gap-4"
        >
          <div className="rounded-2xl bg-white/5 p-4 border border-white/5">
            <Shield className="text-yellow-500 mb-2" size={16} />
            <span className="text-[9px] font-black uppercase tracking-widest text-white/60 block mb-1">Garantía Aura</span>
            <p className="text-[10px] text-white/30 leading-tight">Hardware cubierto ante cualquier fallo técnico.</p>
          </div>
          <div className="rounded-2xl bg-white/5 p-4 border border-white/5">
            <QrCode className="text-purple-500 mb-2" size={16} />
            <span className="text-[9px] font-black uppercase tracking-widest text-white/60 block mb-1">Tu ID Aura</span>
            <p className="text-[10px] text-white/30 leading-tight">Menciónalo siempre para un soporte más rápido.</p>
          </div>
        </motion.div>

        {/* QR Section for the client to know they can spread this */}
        <div className="mt-16 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/20 mb-8">Ecosistema Aura Business</p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/5 bg-white/5 opacity-50">
            <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-[8px] font-bold uppercase tracking-widest">Servidor de Soporte Operativo</span>
          </div>
        </div>
      </div>

      {/* Fullscreen AuraAgent Modal */}
      {showChat && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-yellow-500 flex items-center justify-center">
                <Zap size={16} className="text-black fill-black" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest">Aura Intelligence Agent</span>
            </div>
            <button 
              onClick={() => setShowChat(false)}
              className="px-3 py-1 rounded-full border border-white/10 text-[9px] font-black uppercase tracking-widest hover:bg-white/5"
            >
              Cerrar
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <AuraAgent isEmbedded={true} mode="support" />
          </div>
        </div>
      )}
    </div>
  );
}
